import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsightsGrid = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      // Fetch only published insights (default behavior for public)
      const response = await axios.get(`${API_URL}/api/insights/`);
      // Show only first 3 insights on homepage
      setInsights(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-48 bg-gray-200"></div>
            <CardContent className="p-6">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No insights available at this time.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {insights.map((insight) => (
        <Card key={insight.id} className="group hover:shadow-xl transition-shadow duration-300 overflow-hidden">
          {insight.cover_image && (
            <div className="h-48 overflow-hidden">
              <img
                src={insight.cover_image}
                alt={insight.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <CardContent className="p-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
              <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700">
                {insight.category}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(insight.published_at)}
              </span>
            </div>
            
            <h3 className="text-xl font-serif text-gray-900 mb-3 line-clamp-2 group-hover:text-gray-700 transition-colors">
              {insight.title}
            </h3>
            
            <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
              {insight.excerpt}
            </p>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{insight.author}</span>
              <Link 
                to={`/insights/${insight.id}`}
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
              >
                Read More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            
            {insight.views > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-1 text-xs text-gray-500">
                <Eye className="h-3 w-3" />
                {insight.views} views
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default InsightsGrid;
